import React from 'react';
import {
  ClipboardDocumentListIcon,
  InboxStackIcon,
  CalendarIcon,
  CalendarDaysIcon,
  ClockIcon,
  PencilIcon,
  PlusCircleIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';

const statIconMap = {
  attendance: ClockIcon,
  requests: InboxStackIcon,
  leaves: CalendarDaysIcon,
  tasks: ClipboardDocumentListIcon,
  holidays: CalendarIcon,
  performance: PresentationChartLineIcon,
};

const statIconClassMap = {
  attendance: 'bg-blue-50 text-blue-600',
  requests: 'bg-fuchsia-50 text-fuchsia-600',
  leaves: 'bg-violet-50 text-violet-600',
  tasks: 'bg-cyan-50 text-cyan-600',
  holidays: 'bg-amber-50 text-amber-600',
  performance: 'bg-emerald-50 text-emerald-600',
};

function getInitials(user) {
  const first = user?.firstName?.[0] || '';
  const last = user?.lastName?.[0] || '';
  return `${first}${last}`.toUpperCase() || 'U';
}

export default function EmployeeDashboardStatsHub({
  user,
  stats = [],
  activeSection,
  onSectionChange,
  onApplyLeave,
  onRequestCheckout,
  onRequestExtraHours,
  headerActions,
  todayCard,
  refreshIndicator,
  onProfilePhotoEdit,
  eligibleDatesCount = 0,
}) {
  return (
    <div data-dashboard-layout="wide" className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-6 lg:px-8">
      {refreshIndicator}

      <div
        data-testid="employee-dashboard-premium-header"
        className="mb-4 overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-4 py-4 text-white shadow-xl shadow-slate-900/15 ring-1 ring-white/10"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-200">Employee Dashboard</p>
            <h1 className="mt-1 truncate text-2xl font-black tracking-normal text-white">
              Hi, {user?.firstName || 'Employee'}
            </h1>
            <p className="mt-1 truncate text-sm font-medium text-slate-300">{user?.department || 'Your team'}</p>
          </div>
          <div className="relative shrink-0">
            <div
              data-testid="employee-dashboard-avatar"
              className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-white text-sm font-black text-slate-950 shadow-lg ring-1 ring-white/30"
            >
              {user?.profilePhotoUrl ? (
                <img
                  src={user.profilePhotoUrl}
                  alt={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
                  className="h-full w-full object-cover"
                />
              ) : getInitials(user)}
            </div>
            {onProfilePhotoEdit && (
              <button
                type="button"
                data-testid="employee-dashboard-avatar-edit"
                onClick={onProfilePhotoEdit}
                className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-slate-950 bg-white text-slate-900 shadow-lg transition hover:scale-105 hover:bg-blue-50"
                title="Update profile photo"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        {headerActions && (
          <div
            data-testid="employee-dashboard-header-actions"
            className="mt-4 flex w-full flex-wrap items-center gap-2 overflow-hidden rounded-2xl bg-white/10 p-2 ring-1 ring-white/10"
          >
            {headerActions}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {todayCard}

        <div
          data-testid="employee-dashboard-action-grid"
          className="grid grid-cols-3 gap-3"
        >
          <button
            type="button"
            onClick={onApplyLeave}
            className="flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-2xl border border-violet-200 bg-violet-50 px-2 py-3 text-center text-xs font-black text-violet-700 shadow-sm transition hover:bg-violet-100"
          >
            <span data-testid="employee-dashboard-action-icon" className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-violet-600 shadow-sm">
              <CalendarDaysIcon className="h-5 w-5" />
            </span>
            <span className="leading-tight">Apply Leave</span>
          </button>
          <button
            type="button"
            onClick={onRequestCheckout}
            className="relative flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-2xl border border-orange-200 bg-orange-50 px-2 py-3 text-center text-xs font-black text-orange-700 shadow-sm transition hover:bg-orange-100"
          >
            <span data-testid="employee-dashboard-action-icon" className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-orange-600 shadow-sm">
              <ClockIcon className="h-5 w-5" />
            </span>
            <span className="leading-tight">Checkout Time</span>
            {eligibleDatesCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[22px] h-[22px] px-1.5 bg-red-500 text-white text-[10px] font-black rounded-full shadow-md animate-pulse">
                {eligibleDatesCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onRequestExtraHours}
            className="flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-2 py-3 text-center text-xs font-black text-blue-700 shadow-sm transition hover:bg-blue-100"
          >
            <span data-testid="employee-dashboard-action-icon" className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-sm">
              <PlusCircleIcon className="h-5 w-5" />
            </span>
            <span className="leading-tight">Extra Hours</span>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          {stats.map((tile) => {
            const active = tile.id === activeSection;
            const StatIcon = statIconMap[tile.id] || ClockIcon;
            const iconClass = statIconClassMap[tile.id] || 'bg-slate-50 text-slate-600';

            return (
              <button
                key={tile.id}
                type="button"
                data-section-id={tile.id}
                aria-pressed={active}
                onClick={() => onSectionChange(tile.id)}
                className={`min-h-[104px] rounded-2xl border p-4 text-left shadow-sm transition ${
                  active
                    ? 'border-blue-500 bg-blue-50 text-blue-950 ring-2 ring-blue-200'
                    : 'border-gray-200 bg-white text-gray-950 hover:border-blue-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-2xl font-black leading-none">{tile.value}</div>
                    <div className="mt-2 text-sm font-bold">{tile.label}</div>
                  </div>
                  <div
                    data-testid="employee-dashboard-stat-icon"
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${iconClass}`}
                  >
                    <StatIcon className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 text-xs font-medium text-gray-500">{tile.meta}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
